import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { readPrefs, writePrefs } from './shellState';

/**
 * Below this window width the full sidebar would leave the screens less than their 968px minimum:
 * 232px sidebar + 968px + 16px for an always-visible scrollbar (macOS with a mouse, Windows).
 */
const NARROW_WINDOW_QUERY = '(max-width: 1215px)';

function narrowMedia(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(NARROW_WINDOW_QUERY) : null;
}

function subscribe(onChange: () => void): () => void {
  const media = narrowMedia();
  media?.addEventListener('change', onChange);
  return () => media?.removeEventListener('change', onChange);
}

const isNarrowWindow = (): boolean => narrowMedia()?.matches === true;

/**
 * Sidebar state (decisions-log L9). The user's own choice persists. A narrow window collapses the
 * sidebar without overwriting that choice, so it comes back when the window widens; the toggle can
 * still open it while the window stays narrow.
 */
export function useSidebarCollapsed(): readonly [collapsed: boolean, toggle: () => void] {
  const isNarrow = useSyncExternalStore(subscribe, isNarrowWindow, () => false);
  const [preferred, setPreferred] = useState(() => readPrefs().sidebarCollapsed === true);
  const [openWhileNarrow, setOpenWhileNarrow] = useState(false);
  const [seenNarrow, setSeenNarrow] = useState(isNarrow);

  // Each time the window turns narrow it starts collapsed again (state adjusted during render).
  if (seenNarrow !== isNarrow) {
    setSeenNarrow(isNarrow);
    setOpenWhileNarrow(false);
  }

  useEffect(() => {
    writePrefs({ sidebarCollapsed: preferred });
  }, [preferred]);

  const toggle = useCallback(() => {
    if (isNarrow) setOpenWhileNarrow((open) => !open);
    else setPreferred((value) => !value);
  }, [isNarrow]);

  return [isNarrow ? !openWhileNarrow : preferred, toggle];
}
